import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import type { RenewalRequestDto } from '@his/shared';
import { get, qs } from '@/lib/api';
import { dateOnly, dateTime, money } from '@/lib/utils';
import { DataTable, PageHeader } from '@/components/ui/layout';
import { Button, StatusBadge } from '@/components/ui/primitives';
import { CLIENT_DESK, openDeskAttachment } from '../desk/desk-api';

/**
 * Renewal requests, from the contact person's side.
 *
 * The screenshot is required rather than optional: this request is a claim that
 * money moved, and a claim with no evidence turns into a phone call every time.
 * The date access runs to is not asked for here — the owner sets it when they
 * confirm the money actually arrived.
 */
export function ClientRenewalsPage() {
  const navigate = useNavigate();

  const renewals = useQuery({
    queryKey: ['client-renewals'],
    queryFn: () => get<RenewalRequestDto[]>(`/client/renewals${qs({ limit: 100 })}`),
  });

  return (
    <>
      <PageHeader
        title="Subscription renewals"
        description="Send proof of payment; the owner confirms it and sets the new date"
        actions={<Button onClick={() => navigate('/client/renewals/new')}>New request</Button>}
      />

      <DataTable
        rows={renewals.data}
        loading={renewals.isLoading} refreshing={renewals.isFetching}
        error={renewals.error}
        rowKey={(row) => row.id}
        empty="You have not sent a renewal request yet."
        columns={[
          {
            header: 'Hospital',
            sort: (row) => row.tenantName,
            cell: (row) => (
              <div>
                <div className="font-medium">{row.tenantName}</div>
                <div className="text-xs text-muted-foreground">{dateTime(row.createdAt)}</div>
              </div>
            ),
          },
          { header: 'Amount', align: 'right', sort: (row) => row.amountMinor, cell: (row) => money(row.amountMinor) },
          { header: 'Reference', sort: (row) => row.reference, cell: (row) => row.reference ?? '—' },
          {
            header: 'Status',
            sort: (row) => row.status,
            cell: (row) => (
              <StatusBadge
                status={row.status}
                tone={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'destructive' : 'warning'}
              />
            ),
          },
          {
            header: 'Runs to',
            sort: (row) => row.approvedUntil,
            cell: (row) => (row.approvedUntil ? dateOnly(row.approvedUntil) : '—'),
          },
          {
            header: 'Proof',
            cell: (row) => (
              <div className="flex flex-wrap gap-1">
                {row.attachments.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    className="flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs hover:bg-accent focus-ring"
                    onClick={() => void openDeskAttachment(CLIENT_DESK, file.id)}
                  >
                    <Paperclip className="h-3 w-3" /> view
                  </button>
                ))}
              </div>
            ),
          },
          {
            header: 'Note from the owner',
            sort: (row) => row.reviewNote,
            cell: (row) => row.reviewNote ?? '—',
          },
        ]}
      />

    </>
  );
}
